import { useEffect } from "react";

/**
 * Calls `onEscape` when the user presses Escape, unless a Radix overlay
 * (dialog, sheet, alert-dialog, popover, select, dropdown-menu) is open.
 *
 * The handler may return `false` to decline the key press: propagation is then
 * NOT stopped, so a later-registered `useEscapeCancel` handler (e.g. the
 * page-level "go back") still runs. Returning nothing behaves as before and
 * stops every later handler.
 *
 * `options.capture` registers the listener in the capture phase so it runs
 * before every bubble-phase handler, including ones registered earlier (#1684:
 * a form mounted on a later tab must see Escape before the page's handler). In
 * capture mode the listener also stands down while an in-form widget that
 * handles its own Escape is open (a Select listbox, a DropdownMenu, a Radix
 * popper, or a focused `aria-expanded="true"` trigger), because it would
 * otherwise pre-empt Radix's own capture-phase Escape handling.
 */
export function useEscapeCancel(
  onEscape: (() => void | boolean) | undefined,
  options?: { capture?: boolean },
) {
  const capture = options?.capture ?? false;

  useEffect(() => {
    if (!onEscape) return;

    const handler = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      // Radix portals render with [data-radix-portal]. If any portal is in
      // the DOM a dialog/sheet/select/popover is open and handles its own Escape.
      if (document.querySelector("[data-radix-portal]")) return;

      // Non-Radix modals (role="dialog" or role="alertdialog")
      if (
        document.querySelector('[role="dialog"], [role="alertdialog"]')
      )
        return;

      // Command palette
      if (document.querySelector("[cmdk-root]")) return;

      // Capture mode runs before Radix's own Escape handling: leave the key to
      // an open Select / DropdownMenu / popper or an expanded trigger.
      if (capture) {
        if (
          document.querySelector(
            '[role="listbox"], [role="menu"], [data-radix-popper-content-wrapper]',
          )
        )
          return;
        if (
          event.target instanceof Element &&
          event.target.closest('[aria-expanded="true"]')
        )
          return;
      }

      const handled = onEscape();
      // Prevent other useEscapeCancel handlers from firing on the same
      // keypress (e.g. page-level + form-level both registered), unless the
      // handler declined it.
      if (handled !== false) event.stopImmediatePropagation();
    };

    document.addEventListener("keydown", handler, capture);
    return () => document.removeEventListener("keydown", handler, capture);
  }, [onEscape, capture]);
}
